import { useCallback, useMemo, useState } from 'react';
import requestService from '../services/requestService';
import {
    normalizeMessages,
    normalizeRequest,
    normalizeShops,
} from '../utils/requestHelpers';
import { NOTIFY_SHOP_IDS_KEY } from '../utils/requestConstants';

const useRequestDetail = (id) => {
    const [loading, setLoading] = useState(false);
    const [errorMsg, setErrorMsg] = useState('');
    const [request, setRequest] = useState(normalizeRequest(null));

    const [messages, setMessages] = useState([]);
    const [msgLoading, setMsgLoading] = useState(false);
    const [msgError, setMsgError] = useState('');

    const [shops, setShops] = useState([]);
    const [participantsLoading, setParticipantsLoading] = useState(false);
    const [participantsError, setParticipantsError] = useState('');

    const fetchRequest = useCallback(async () => {
        try {
            setLoading(true);
            setErrorMsg('');
            const data = await requestService.getRequest(id);
            setRequest(normalizeRequest(data));
        } catch (e) {
            setErrorMsg(e?.response?.data?.message || e?.message || 'Failed to load request');
            setRequest(normalizeRequest(null));
        } finally {
            setLoading(false);
        }
    }, [id]);

    const fetchMessages = useCallback(async () => {
        try {
            setMsgLoading(true);
            setMsgError('');
            const data = await requestService.getMessages(id);
            setMessages(normalizeMessages(data));
        } catch (e) {
            setMsgError(e?.response?.data?.message || e?.message || 'Failed to load conversation');
            setMessages([]);
        } finally {
            setMsgLoading(false);
        }
    }, [id]);

    const fetchParticipants = useCallback(async () => {
        try {
            setParticipantsLoading(true);
            setParticipantsError('');
            const data = await requestService.getParticipants(id);
            setShops(normalizeShops(data));
        } catch (e) {
            setParticipantsError(e?.response?.data?.message || e?.message || 'Failed to load shops');
            setShops([]);
        } finally {
            setParticipantsLoading(false);
        }
    }, [id]);

    const handleChangeStatus = useCallback(async (nextStatus) => {
        await requestService.updateStatus(id, nextStatus);
        await fetchRequest();
        await fetchMessages();
    }, [id, fetchRequest, fetchMessages]);

    const handleSend = useCallback(async ({ content, visibility, notifyCustomer, notifyShopIds }) => {
        await requestService.sendMessage(id, {
            content,
            visibility,
            notifyCustomer: !!notifyCustomer,
            [NOTIFY_SHOP_IDS_KEY]: Array.isArray(notifyShopIds) ? notifyShopIds : [],
        });
        await fetchMessages();
        await fetchRequest();
    }, [id, fetchMessages, fetchRequest]);

    const addShops = useCallback(async (shopIds) => {
        if (!shopIds || shopIds.length === 0) return;
        await requestService.addParticipants(id, shopIds);
        await fetchParticipants();
        await fetchMessages();
    }, [id, fetchMessages, fetchParticipants]);

    const removeShop = useCallback(async (participantId) => {
        if (!participantId) return;
        await requestService.removeParticipant(id, participantId);
        await fetchParticipants();
        await fetchMessages();
    }, [id, fetchMessages, fetchParticipants]);

    const existingShopIds = useMemo(
        () => (shops || []).map((s) => s.shopId).filter(Boolean),
        [shops]
    );

    return {
        loading,
        errorMsg,
        request,

        messages,
        msgLoading,
        msgError,

        shops,
        participantsLoading,
        participantsError,
        existingShopIds,

        fetchRequest,
        fetchMessages,
        fetchParticipants,

        handleChangeStatus,
        handleSend,
        addShops,
        removeShop,
    };
};

export default useRequestDetail;